import { format, parseISO, isValid } from 'date-fns';
import { Check, Mail, Pause, X } from 'lucide-react';
import { useId } from 'react';
import { Text, COLORS } from '@summer/ui-components';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@summer/ui-mosaic';

const completedStatus = 'COMPLETED' as const;
const startedStatus = 'STARTED' as const;
const failedStatus = 'FAILED' as const;
const cancelledStatus = 'CANCELLED' as const;
const expiredStatus = 'EXPIRED' as const;

type EmailChange = {
  id: string;
  createdAt: string;
  changeFinishedAt: string | null;
  changeStatus: string;
  oldEmail: string;
  newEmail: string;
  requiresAuthentication: boolean;
  authenticatedBy: string | null;
};

const Timestamp = ({ isoDate }: { isoDate: string }) => {
  const date = parseISO(isoDate);
  if (!isValid(date)) return 'N/A';
  return format(date, "MMM d, yyyy 'at' h:mm a");
};

const getActionText = (status: string) => {
  if (status === completedStatus) return 'Email Updated';
  if (status === startedStatus) return 'Email Update Started';
  if (status === failedStatus) return 'Email Update Failed';
  if (status === cancelledStatus) return 'Email Update Cancelled';
  if (status === expiredStatus) return 'Email Update Expired';
};

const iconContainerBaseClasses =
  'flex items-center justify-center w-6 h-6 rounded-lg';

const StatusIcon = ({ status }: { status: string }) => {
  if (status === completedStatus) {
    return (
      <div
        className={iconContainerBaseClasses}
        style={{ backgroundColor: COLORS.lightGreen }}
      >
        <Check size={16} color={COLORS.darkGreen} />
      </div>
    );
  }
  if (status === startedStatus) {
    return (
      <div
        className={iconContainerBaseClasses}
        style={{ backgroundColor: COLORS.lightYellow }}
      >
        <Pause size={16} color={COLORS.brownOrange} />
      </div>
    );
  }
  return (
    <div
      className={iconContainerBaseClasses}
      style={{ backgroundColor: COLORS.lightPink }}
    >
      <X size={8} color={COLORS.magentaDark} />
    </div>
  );
};

type EmailUpdateHistoryProps = {
  emailChanges?: EmailChange[];
};

const EmailUpdateHistory = ({ emailChanges }: EmailUpdateHistoryProps) => {
  const baseId = useId();
  const history = emailChanges ?? [];

  if (history.length === 0) {
    return null;
  }

  return (
    <div className="my-6">
      <Accordion type="single" collapsible>
        <AccordionItem value="email-history">
          <AccordionTrigger>
            <div className="pl-3 flex row justify-between align-center line-height-2 gap-2">
              <div className={iconContainerBaseClasses}>
                <Mail size={16} color={COLORS.darkestGrey} />
              </div>
              <h2 id={`${baseId}-update-history`}>Email Update History</h2>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="overflow-x-auto mt-4">
              <table
                className="w-full border-collapse"
                aria-labelledby={`${baseId}-update-history`}
                role="table"
              >
                <thead>
                  <tr className="border-b" style={{ borderColor: COLORS.grey }}>
                    <th
                      className="text-left p-3 font-semibold"
                      style={{ color: COLORS.darkGrey }}
                      scope="col"
                    >
                      Action & Application
                    </th>
                    <th
                      className="text-left p-3 font-semibold"
                      style={{ color: COLORS.darkGrey }}
                      scope="col"
                    >
                      Email Change
                    </th>
                    <th
                      className="text-left p-3 font-semibold"
                      style={{ color: COLORS.darkGrey }}
                      scope="col"
                    >
                      Timestamp
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(change => (
                    <tr
                      key={change.id}
                      className="border-b"
                      style={{ borderColor: COLORS.grey }}
                    >
                      <td className="p-3 align-top">
                        <div className="flex items-center gap-2">
                          <StatusIcon status={change.changeStatus} />
                          <Text>{getActionText(change.changeStatus)}</Text>
                        </div>
                      </td>
                      <td className="p-3 align-top">
                        <div className="flex flex-col gap-1">
                          <Text>{change.oldEmail}</Text>
                          <Text size="small" color="secondary">
                            {change.newEmail}
                          </Text>
                        </div>
                      </td>
                      <td className="p-3 align-top">
                        <Timestamp
                          isoDate={change.changeFinishedAt ?? change.createdAt}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default EmailUpdateHistory;
